import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useSmoothScroll } from '../hooks/useSmoothScroll';
import { services } from '../mock';
import { Cpu, Container, Code, Database, Server, Network, Check, ArrowRight } from 'lucide-react';
import CTASection from '../components/CTASection';

const iconMap = {
  Cpu: Cpu,
  Container: Container,
  Code: Code,
  Database: Database,
  Server: Server,
  Network: Network,
};

const ProductsPage = () => {
  useSmoothScroll();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <div className="bg-slate-950">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f15_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f15_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 mb-6"
            >
              Solusi Monitoring Terlengkap
            </motion.span>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Produk & Layanan <span className="bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">WhaTap</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Platform observability lengkap untuk semua kebutuhan monitoring infrastruktur modern Anda
            </p>
          </motion.div>
        </div>
      </section>

      {/* Detailed Services */}
      <section ref={ref} className="py-20 bg-slate-950">
        <div className="container mx-auto px-6 md:px-12">
          <div className="space-y-32">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="inline-flex items-center gap-3 mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-2xl flex items-center justify-center">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <span className="text-emerald-400 font-semibold">0{service.id}</span>
                    </div>
                    
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                      {service.title}
                    </h2>
                    <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features list */}
                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 text-emerald-400" />
                          </div>
                          <span className="text-slate-300 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05, gap: 12 }}
                      whileTap={{ scale: 0.95 }}
                      className="group flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-full font-semibold shadow-lg shadow-emerald-500/25"
                    >
                      Pelajari Lebih Lanjut
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                  </div>

                  {/* Visual */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4 }}
                      className="relative"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-blue-500/20 rounded-3xl blur-2xl" />
                      <div className="relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-3xl p-12 aspect-square flex items-center justify-center">
                        <Icon className="w-48 h-48 text-emerald-400/30" />
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-blue-500/5 rounded-3xl" />
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default ProductsPage;
